import { select, withQuery } from '@panel/commons/core/themev2';
import styled, { css } from 'styled-components';
import { getColor } from '@panel/commons/core/themev3';
import AlertCircleBlue from '@panel/commons/assets/icons/AlertCircleBlue.svg';
import { site } from '@panel/commons/helpers';
import checkMark from '../../../../assets/img/checkbox-check-mark.svg';
import CartIcon from '../../../../assets/img/cart-icon.svg';
import editIcon from '../../../../assets/img/edit.svg';
import editIconVA from '../../../../assets/img/edit_VA.svg';

const siteId = site.id;
const iconEdit = siteId === 'VAMX' ? editIconVA : editIcon;

const Icons = {
  cart: CartIcon
};

const Step4Module2Container = styled.div`
  display: flex;
  gap: 40px;
  padding: 40px 40px 45px;
  position: relative;

  ${withQuery.sm.down()} {
    flex-direction: column;
    padding: 0;
  }
`;

const FormContainer = styled.div`
  width: 100%;
`;

const ReadOnlyContainer = styled.div`
  max-width: fit-content;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 4px;
  background-color: ${select('midLightGray')};
  color: ${select('darkGray')};
  padding: 16px;
  border-radius: 8px;
  margin: 0 0 40px 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 22.41px;
`;

const mainTitleStyles = css`
  font-size: 20px;
  white-space: normal;
`;

const wireTransferTitleStyles = css`
  font-size: 24px;
  font-weight: 700;
  white-space: normal;
`;

const secondaryTitleStyles = css`
  font-size: 16px;
  font-weight: 600;
  margin-top: 24px;
`;

const subTitleStyles = css`
  font-size: 14px;
  color: ${select('darkGray')};
  font-weight: 400;
  margin-bottom: ${({ withNotice }) => (withNotice ? '12px' : '24px')};
  span {
    color: #fc7b27;
  }
`;

const Title = styled.h2`
  white-space: normal;
  ${({ main }) => main && mainTitleStyles}
  ${({ secondary }) => secondary && secondaryTitleStyles}
  ${({ subtitle }) => subtitle && subTitleStyles}
  ${({ wireTransfer }) => wireTransfer && wireTransferTitleStyles}
  ${({ icon }) =>
    icon &&
    css`
      ::before {
        content: url(${Icons[icon]});
        width: 16px;
        height: 13px;
        margin-right: 8px;
      }
    `}
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: ${({ isRequestError }) => (isRequestError ? 'flex-end' : 'initial')};
  gap: 16px;

  ${withQuery.sm.down()} {
    flex-direction: column;
    align-items: normal;
    gap: 0px;
  }
`;

const NestedRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-grow: 1;
  flex-basis: 100%;
  gap: 16px;

  input,
  select {
    width: 100%;
  }
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isCheckbox }) => (isCheckbox ? 'row' : 'column')};
  flex-grow: 1;
  flex-basis: ${({ small }) => (small ? '50%' : '100%')};
  margin-bottom: ${({ isTerms }) => (isTerms ? 'unset' : '24px')};
  margin-top: ${({ isTerms }) => (isTerms ? '3%' : 'unset')};
  position: relative;
  justify-content: ${({ isTerms }) => (isTerms ? 'unset' : 'center')};

  label {
    width: fit-content;
    color: ${({ disabled, coupon }) => {
      if (disabled) {
        return select('midLightGray');
      }
      if (coupon) {
        return select('positive');
      }
      return select('black');
    }};
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 4px;
    line-height: 22px;
  }
`;

const selectStyles = css`
  cursor: pointer;
  appearance: none;
  color: ${({ disabled }) => (disabled ? select('midLightGray') : select('black'))};
  line-height: 1.15;
  -webkit-appearance: none;
  -moz-appearance: none;
  -o-appearance: none;

  :invalid {
    color: ${select('darkGray')};
  }

  option {
    text-overflow: ellipsis;
    color: black;
  }

  option:first-of-type {
    color: ${select('darkGray')};
  }
`;

const SelectArrow = styled.span`
  position: absolute;
  display: block;
  border-bottom: 1px solid ${({ disabled }) => (disabled ? select('midLightGray') : select('black'))};
  border-left: 1px solid ${({ disabled }) => (disabled ? select('midLightGray') : select('black'))};
  height: 7px;
  width: 7px;
  transform: rotate(-45deg);
  right: 15px;
  top: 44px;
  pointer-events: none;
`;

const checkboxStyles = css`
  height: 16px;
  min-width: 16px;
  position: relative;
  padding: unset;
  opacity: 0;
  cursor: pointer;

  & ~ span {
    display: flex;
    border: 1.5px solid black;
    border-radius: 2px;
    height: 16px;
    width: 16px;
    pointer-events: none;
    position: absolute;
    box-sizing: border-box;
  }

  :checked ~ span {
    background-color: ${getColor('primary')};
    background-image: url(${checkMark});
    background-repeat: no-repeat;
    background-size: 8px 6px;
    background-position: 45% 40%;
  }
`;

const TextWithLink = styled.p`
  font-size: 14px;
  font-weight: 400;
  margin-left: 8px;
  line-height: 1;
  padding-top: 2px;
  white-space: normal;
  a {
    color: ${getColor('primary')};
    margin: 0 3px 0 3px;
    text-decoration: none;
  }
`;

const Field = styled.div`
  padding: 15px 13px 15px 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ disabled, error, coupon }) => {
      if (disabled) {
        return 'transparent';
      }
      if (error) {
        return select('negative');
      }
      if (coupon) {
        return select('positive');
      }
      return select('midDarkGray');
    }};
  ${({ disabled, coupon }) =>
    disabled &&
    coupon &&
    css`
      color: ${select('midLightGray')};
    `};
  outline: none;
  background-color: ${({ disabled }) => (disabled ? select('lightGray') : select('white'))};
  max-height: 48px;
  max-width: ${({ name }) => (name === 'coupon' ? '179px' : 'unset')};
  box-sizing: border-box;
  -moz-appearance: textfield;

  ::-webkit-inner-spin-button {
    display: none;
  }

  &::placeholder {
    color: ${({ disabled }) => (disabled ? select('midLightGray') : select('darkGray'))};
    font-family: 'Hind';
  }

  &:focus::placeholder {
    color: transparent;
    transition: 200ms;
  }

  &:focus {
    border-color: ${getColor('primary')};
  }

  ${({ as }) => as === 'select' && selectStyles}

  ${({ type }) => type === 'checkbox' && checkboxStyles}
  ${withQuery.sm.down()} {
    max-width: ${({ name }) => (name === 'coupon' ? '95%' : 'unset')};
  }
`;

const generalErrorStyles = css`
  position: initial;
  text-align: right;
  margin-top: 5px;
`;

const FieldError = styled.p`
  font-weight: 500;
  font-size: 12px;
  white-space: normal;
  line-height: 1;
  color: ${select('negative')};
  margin-left: 8px;
  position: ${({ isRequestError }) => (isRequestError ? 'block' : 'absolute')};
  top: ${({ isCheckbox }) => (isCheckbox ? '110%' : '105%')};
  left: ${({ isCheckbox }) => (isCheckbox ? '17px' : 'unset')};
  ${({ generalError }) => generalError && generalErrorStyles}
`;

const PaymentFieldSet = styled.fieldset`
  border: none;
  margin-bottom: 16px;
  padding: 0;
  &:disabled {
    opacity: 0.3;
    pointer-events: none;
  }
`;

const stickyDetail = css`
  position: fixed;
  bottom: 64px;
  margin: 0px;
  box-sizing: border-box;
  width: 100%;
  left: 0;
  top: unset;
`;

const mobileDetail = css`
  position: relative;
  transition: all 1ms ease-in-out;
  gap: 16px;
  width: unset;
  box-sizing: initial;
  margin-bottom: 40px;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ isSticky }) => (isSticky ? '0' : '20px')};
  position: sticky;
  max-width: 272px;
  width: 100%;
  height: fit-content;
  background-color: white;
  border: 1px solid ${select('lightGray')};
  border-radius: 8px;
  box-shadow: 1px 2px 16px rgba(0, 0, 0, 0.05);
  padding: 18px 16px 4px 16px;
  top: 0;
  box-sizing: border-box;

  ::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 6px;
    border-radius: 8px 8px 0 0;
    background-color: ${select('middleGray')};
    right: 0;
    top: -1px;
  }

  ${withQuery.sm.down()} {
    max-width: unset;
    margin-bottom: 24px;
    ${({ isSticky }) => (isSticky ? stickyDetail : mobileDetail)}
  }
`;

const DetailItemsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ isSticky }) => (isSticky ? 'flex-end' : 'space-between')};
  margin-top: 0;
  &.capa {
    margin-top: -10px;
  }
  &.totalWithoutCoupon {
    margin-bottom: 24px;
  }
  ${withQuery.sm.down()} {
    &.taxes {
      margin-bottom: 25px;
    }
    &.total {
      margin-top: 10px;
    }
  }
`;

const wrappedItemStyles = css`
  font-size: 12px;
  line-height: 19.21px;
  flex-basis: 100%;
  margin-top: 4px;
`;

const priceItemStyles = css`
  font-size: 20px;
  font-weight: 600;
  span {
    font-size: 14px;
    font-weight: 400;
  }
`;

const upgradeStyles = css`
  span {
    font-weight: 600;
  }
`;
const capaItemStyles = css`
  font-size: 12px;
  color: ${select('darkGray')};
`;

const DetailItem = styled.div`
  color: ${({ color }) => (color ? select(color) : 'initial')};
  margin-right: 7px;
  ${({ wrapped }) => wrapped && wrappedItemStyles};
  ${({ price }) => price && priceItemStyles}
  ${({ upgrade }) => upgrade && upgradeStyles}
  ${({ capa }) => capa && capaItemStyles}
`;

const Separator = styled.span`
  border-radius: 1.5px;
  width: 100%;
  height: 1px;
  background-color: ${select('middleGray')};
  ${withQuery.sm.down()} {
    &.capa {
      margin: 24px 0;
    }
  }
`;

const footNoteStyles = css`
  position: relative;
  font-size: 12px;
  font-weight: 400;
  line-height: 19.21px;
  margin-top: 36px;
  a {
    color: ${getColor('primary')};
    margin-left: 3px;
    text-decoration: none;
  }
  ::before {
    content: '*';
    position: absolute;
    left: -8px;
    font-size: 12px;
    font-weight: 400;
  }
`;

const blueNoticeStyles = css`
  margin-bottom: 16px;
  font-size: 13px;
  background-color: #c2d7f7;
  padding: 8px 10px;
  border-radius: 4px;
  span {
    font-weight: 500;
  }
`;

const InfoBillingNotice = styled.div`
  margin-bottom: 16px;
`;

const Notice = styled.div`
  white-space: initial;
  color: ${({ color }) => (color ? select(color) : 'initial')};
  ${({ blueNote }) => blueNote && blueNoticeStyles}
  ${({ footNote }) => footNote && footNoteStyles};
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${getColor('primary')};
  align-self: flex-end;
  font-weight: 600;
  margin-bottom: 3px;
  &::after {
    margin-left: 5px;
    margin-top: -5px;
  }
`;

const ContainerToggle = styled.div`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  flex-direction: column;
  gap: 24px;
`;

const DetailsContainerToObserve = styled.div`
  max-width: 272px;
  width: 100%;
  display: ${({ show = true }) => (show ? 'unset' : 'none')};
  ${withQuery.sm.down()} {
    max-width: unset;
    width: 100%;
  }
`;

const CompletedPaymentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 20px 0 20px 0;
  img {
    margin-bottom: 32px;
    width: 158.78px;
    height: 107.23px;
  }
  h3 {
    color: ${({ isSuccess }) => (isSuccess ? select('positive') : select('negative'))};
    font-weight: 700;
    font-size: 24px;
    line-height: 31px;
    margin-bottom: 12px;
    white-space: ${({ isBrasil }) => (isBrasil ? 'normal' : 'inherit')};
    text-align: ${({ isBrasil }) => (isBrasil ? 'center' : 'inherit')};
  }
  p {
    font-weight: 400;
    font-size: 16px;
    width: ${({ isSuccess }) => (isSuccess ? '250px' : '325px')};
    text-align: center;
    white-space: normal;
  }
`;

const StyledLink = styled.div`
  display: flex;
  align-items: center;
  color: ${getColor('primary')};
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  flex: 0 0 100%;
  &::after {
    content: url(${iconEdit});
    height: 12px;
    width: 12px;
    display: inline-block;
    margin-left: 9px;
    margin-top: -5px;
  }
`;

const TextInfo = styled.p`
  font-size: 16px;
  white-space: normal;
  max-width: 558px;
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  h3 {
    color: ${select('positive')};
    font-size: 24px;
    font-weight: 700;
    margin: 30px 0 8px;
  }
  div {
    margin-bottom: 8px;
    p {
      max-width: 426px;
      font-size: 16px;
      text-align: center;
      white-space: normal;
    }
  }
`;

const NumberContact = styled.div`
  display: inline-flex;
  font-weight: 600;
`;

const PaymentContainer = styled.div`
  margin-top: 20px;
  ${withQuery.sm.down()} {
    height: fit-content;
  }
`;

const PaymentRow = styled.div`
  &.wireTransfer {
    padding-top: 12px;
    border-top: 3px solid ${select('middleGray')};
    margin-bottom: 25px;
    ${withQuery.sm.down()} {
      height: fit-content;
    }
    ${withQuery.xs.down()} {
      margin-bottom: 20px;
    }
  }
  ${withQuery.xs.up()} {
    min-width: 525px;
  }
  ${withQuery.xs.down()} {
    min-width: 210px;
  }
`;

const PaymentButton = styled.div`
  height: 48px;
  width: 100%;
  margin: 0px;
  order: 0;
  border: 1px solid ${getColor('primary')};
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 13px 0;
  cursor: pointer;
  span {
    min-width: 150px;
    font-family: 'Hind';
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    color: ${select('black')};
    ${withQuery.xs.down()} {
      min-width: auto;
    }
  }
  :disabled {
    opacity: 45%;
  }
`;

const WireTransferInfoContainer = styled.div`
  border: 1px solid ${select('midLightGray')};
  border-radius: 20px;
  padding: 16px 16px 24px 16px;
  margin-top: 24px;
  ${withQuery.xs.down()} {
    border: none;
  }
`;

const LogoBankPanama = styled.div`
  background-image: url(${({ src }) => src});
  width: ${props => (props.general ? '130px' : '94px')};
  height: 26px;
  margin-bottom: 15px;
`;

const WireTransferInfoBankContainer = styled.div`
  margin-top: 24px;
`;

const WireTransferRow = styled.div`
  display: flex;
  margin-bottom: ${props => (props.check ? '0' : '8px')};
  white-space: normal;
  span {
    font-size: 16px;
    font-weight: 600;
  }
  p:nth-child(1) {
    font-size: 16px;
    font-weight: 400;
    margin-right: 4px;
  }
  p:nth-child(2) {
    font-size: 16px;
    font-weight: 600;
  }
`;

const InfoCard = css`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  padding: 12px;
  background-color: ${select('lightGray')};
  border: 1px solid ${select('midLightGray')};
  border-left: 4px solid ${select('blue')};
  border-radius: 4px;
  white-space: normal;
  margin-top: 24px;
  &:before {
    content: '';
    vertical-align: text-top;
    width: 16px;
    height: 16px;
    margin-right: 9px;
    position: absolute;
    background-image: url(${AlertCircleBlue});
    background-repeat: no-repeat;
    top: 16px;
    ${withQuery.xs.down()} {
      align-items: baseline;
      top: 16px;
    }
  }
  p {
    margin-left: 25px;
    font-weight: 400;
    > span {
      font-weight: 600;
    }
  }
`;

const WireTransferMessage = styled.div`
  p {
    font-size: 16px;
  }
  ${InfoCard}
`;

const InfoBilling = styled.div`
  p {
    font-size: 14px;
  }
  ${InfoCard}
`;

export {
  Step4Module2Container,
  DetailsContainer,
  DetailItemsContainer,
  DetailItem,
  ReadOnlyContainer,
  FormContainer,
  Title,
  FieldRow,
  NestedRow,
  FieldWrapper,
  Field,
  PaymentFieldSet,
  FieldError,
  SelectArrow,
  TextWithLink,
  InfoBillingNotice,
  Notice,
  Separator,
  ToggleButton,
  ContainerToggle,
  DetailsContainerToObserve,
  CompletedPaymentContainer,
  StyledLink,
  TextInfo,
  SuccessContainer,
  NumberContact,
  PaymentContainer,
  PaymentRow,
  PaymentButton,
  WireTransferInfoContainer,
  WireTransferInfoBankContainer,
  WireTransferRow,
  WireTransferMessage,
  LogoBankPanama,
  InfoBilling
};
